import { type ReactNode } from 'react';

import styles from './Markdown.module.css';

function getSafeHref(url: string): string | null {
  if (
    url.startsWith('https://') ||
    url.startsWith('mailto:') ||
    url.startsWith('/')
  ) {
    return url;
  }

  return null;
}

function normalizeContent(content: string): string {
  return content
    .replace(/\r\n/g, '\n')
    .replace(/\\\./g, '.')
    .replace(/^\s*-\s+Top of Form\s*$/gm, '')
    .replace(/^\s*Top of Form\s*$/gm, '')
    .replace(/^\s*\.\s*$/gm, '')
    .replace(/\*\*_\s*\n\s*([^\n]+?)_\*\*/g, '## $1')
    .replace(/\*\*\s*\n\s*([^\n]+?)\s*\*\*:?/g, '## $1')
    .replace(/^\s*\*\*([A-Z][A-Z0-9\s&().,'/-]+)\*\*\s*$/gm, '# $1')
    .replace(/^\s*-\s+\*\*_?([^*\n:]+?)_?\*\*\s*$/gm, '## $1')
    .replace(/<([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})>/gi, '[$1](mailto:$1)')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function parseInline(text: string): ReactNode[] {
  const regex =
    /(\*\*(.*?)\*\*|__(.*?)__|_([^_]+)_|\*([^*\n]+)\*|\[(.*?)\]\((.*?)\))/g;
  const result: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;

  match = regex.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    if (match[2]) {
      // **bold**
      result.push(<strong key={keyCounter++}>{parseInline(match[2])}</strong>);
    } else if (match[3]) {
      // __bold__
      result.push(<strong key={keyCounter++}>{parseInline(match[3])}</strong>);
    } else if (match[4]) {
      // _italic_
      result.push(<em key={keyCounter++}>{parseInline(match[4])}</em>);
    } else if (match[5]) {
      // *italic*
      result.push(<em key={keyCounter++}>{parseInline(match[5])}</em>);
    } else if (match[6] && match[7]) {
      // [text](url)
      const url = getSafeHref(match[7]);

      if (url) {
        const isExternal = url.startsWith('https://');

        result.push(
          <a
            href={url}
            key={keyCounter++}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            target={isExternal ? '_blank' : undefined}
          >
            {parseInline(match[6])}
          </a>,
        );
      } else {
        result.push(parseInline(match[6]));
      }
    }

    lastIndex = regex.lastIndex;
    match = regex.exec(text);
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
}

interface IMarkdownProps {
  content: string;
}

export function Markdown({ content }: IMarkdownProps) {
  const normalizedContent = normalizeContent(content);
  const blocks = normalizedContent.split(/\n\s*\n/);
  let keyCounter = 0;

  return (
    <div className={styles.markdownContent}>
      {blocks.map((block) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        // Check if list (simple heuristic: starts with "- " or "* ")
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
          const items = trimmed.split(/\n(?=\s*[-*]\s+)/);

          return (
            <ul key={keyCounter++}>
              {items.map((item) => {
                const itemContent = item
                  .replace(/^\s*[-*]\s+/, '')
                  .replace(/\n+/g, ' ')
                  .trim();

                return (
                  <li key={`${trimmed}-${itemContent}`}>
                    {parseInline(itemContent)}
                  </li>
                );
              })}
            </ul>
          );
        }

        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)/);
        if (headingMatch) {
          const level = headingMatch[1].length as 1 | 2 | 3 | 4 | 5 | 6;
          const tags = {
            1: 'h1',
            2: 'h2',
            3: 'h3',
            4: 'h4',
            5: 'h5',
            6: 'h6',
          } as const;
          const Tag = tags[level];

          return <Tag key={keyCounter++}>{parseInline(headingMatch[2])}</Tag>;
        }

        return <p key={keyCounter++}>{parseInline(trimmed)}</p>;
      })}
    </div>
  );
}
